'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { fmt } from './ui';

interface Wall {
  title: string;
  subtitle?: string;
  tiles: { image: string; href: string; label: string }[];
}

/** Rotating wall of material swatches, the v2 hero carousel. Pausable, with visible controls. */
export function HeroSwatchWall({ walls, autoPlayMs, wallLabel }: { walls: Wall[]; autoPlayMs: number; wallLabel: string }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const on = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const count = walls.length;
  const go = useCallback((dir: number) => setIndex((i) => (i + dir + count) % count), [count]);

  useEffect(() => {
    if (paused || hovered || reducedMotion || count < 2) return;
    const t = setInterval(() => go(1), autoPlayMs);
    return () => clearInterval(t);
  }, [paused, hovered, reducedMotion, count, autoPlayMs, go]);

  if (count === 0) return null;
  const wall = walls[index] ?? walls[0];
  // A full wall (10 swatches) uses the mosaic layout; smaller walls fall back to an even grid of the same height.
  const full = wall.tiles.length >= 10;
  const rows = full ? 3 : Math.ceil(Math.min(wall.tiles.length, 12) / 4);

  return (
    <div className="flex flex-col gap-3.5" aria-roledescription="carousel" aria-label="Featured materials"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)} onBlurCapture={() => setHovered(false)}>
      <div
        className="grid h-[248px] grid-cols-3 grid-rows-2 gap-2 md:h-[594px] md:grid-cols-4 md:gap-3 md:[grid-template-rows:repeat(var(--rows),minmax(0,1fr))]"
        style={{ '--rows': rows } as React.CSSProperties}
      >
        {wall.tiles.slice(0, full ? 10 : 12).map((tile, k) => (
          <Link
            key={`${index}-${tile.href}-${k}`}
            style={{ animation: 'hvFade .5s ease both', animationDelay: `${k * 40}ms` }}
            href={tile.href}
            className={`group relative overflow-hidden rounded-xl bg-hv-card ${k === 0 ? 'row-span-2' : ''} ${full && k === 5 ? 'md:row-span-2' : ''} ${!full && k === 0 ? 'md:row-span-1' : ''} ${k >= 5 ? 'hidden md:block' : ''}`}
            aria-label={tile.label}
          >
            <Image src={tile.image} alt={tile.label} fill sizes="(min-width: 768px) 220px, 33vw" className="object-cover transition duration-500 group-hover:scale-105" priority={k < 4} />
            <span className="absolute bottom-2 left-2 rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium opacity-0 transition group-hover:opacity-100">{tile.label}</span>
          </Link>
        ))}
      </div>
      <div className="flex items-center gap-2 md:gap-3.5">
        <div className="flex flex-grow flex-col">
          <span className="text-[13px] font-semibold md:text-[17px]">{fmt(wallLabel, { index: index + 1, total: count, title: wall.title })}</span>
          {wall.subtitle && <span className="hidden text-[13px] text-hv-soft md:block">{wall.subtitle}</span>}
        </div>
        {count > 1 && (
          <>
            <CtlButton label="Previous wall" onClick={() => go(-1)}><path d="M15 5l-7 7 7 7" /></CtlButton>
            <CtlButton label={paused ? 'Play' : 'Pause'} onClick={() => setPaused((p) => !p)}>
              {paused ? <path d="M8 5l11 7-11 7z" /> : <><path d="M8 5v14" /><path d="M16 5v14" /></>}
            </CtlButton>
            <CtlButton label="Next wall" onClick={() => go(1)}><path d="M9 5l7 7-7 7" /></CtlButton>
            <div className="hidden gap-1.5 md:flex">
              {walls.map((_, k) => (
                <button key={k} aria-label={`Go to wall ${k + 1}`} onClick={() => setIndex(k)}
                  className={`h-1.5 rounded-sm transition-all ${k === index ? 'w-7 bg-hv-orange' : 'w-1.5 bg-hv-line'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CtlButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} onClick={onClick}
      className="flex h-11 w-11 items-center justify-center rounded-xl border border-hv-line bg-white transition hover:border-hv-ink">
      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
    </button>
  );
}
